import { useState, useEffect, useContext, useRef } from "react";
import { FormContainer as Container, FormSection as Section, InputField as Input, FunctionButton } from "../../components";
import { callAPI, fetchData, sendUpload } from "../../api/apiClient";
import { useInjectFormProps } from "../../hooks/useInjectInputs.js";
import FormContext from "../../context/FormContext.js";
import { ALLOWED_FILE_TYPES } from "../../utils/constants.js";

import './FileUploads.css';

function FileUploads() {
    const { formData, updateFormData } = useContext(FormContext);
    const { injectFormProps } = useInjectFormProps();
    const [selectOptions, setSelectOptions] = useState([]);
    const [fileUploads, setFileUploads] = useState(formData.fileUploads ? formData.fileUploads : []); // {docName: "Document Name", docCode: "code", files: []}
    const fileInputRef = useRef(null);
    const MAX_FILE_SIZE_MB = 5;
    const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024; // 5 MB in bytes

    useEffect(() => {
        if (!formData.fileUploadID) {
            createFileUploadID();
        }
    }, [])

    useEffect(() => {
        getDocTypesAndDocuments();
    }, []);

    const createFileUploadID = () => {
        const tempUploadID = Array(3).fill(0).map(() => Math.random().toString(36).slice(2)).join('').slice(0, 32);
        updateFormData({ fileUploadID: tempUploadID });
    }

    const getDocTypesAndDocuments = async () => {
        try {
            const docTypeResponse = await fetchData("Lkp_Document_Type", { Disabled: 0 });
            const sortedDocTypes = docTypeResponse.sort((a, b) => a.Sort_Order - b.Sort_Order);
            const documentsResponse = await fetchData("Ref_Document", { Disabled: 0 });

            const docDisplayGroup = formData["workingWithSWIS"] === "1" ? "2" : "1"; //Group 1 for general public, group 2 for working with SWIS. 3 for both.
            // Group documents by type
            const groupedOptions = sortedDocTypes.map((category) => ({
                grouped: true,
                label: category.Description,
                value: category.Code,
                options: documentsResponse
                    .filter(doc => (doc.Document_Type_Code === category.Code && (doc.Display_Group === docDisplayGroup || doc.Display_Group === "3")))
                    .map(doc => ({
                        label: doc.Description,
                        value: doc.Code
                    }))
            })).filter(group => group.options.length > 0);// Filter out groups with no options

            // Set the grouped options in state
            setSelectOptions(groupedOptions);
        } catch (error) {
            console.error("Error fetching document types or documents:", error);
        }
    };


    const addFileUploadContainer = (target, name, fileCode) => {
        if (!fileCode) return;

        target.value = ""; // Reset the select input after adding

        // If the file upload already exists, do not add it again
        if (fileUploads.some(file => file.docCode === fileCode)) return;

        const updatedFileUploads = [
            ...fileUploads,
            { docName: name, docCode: fileCode, files: [] }
        ];

        setFileUploads(updatedFileUploads);

        updateFormData({ fileUploads: updatedFileUploads, });
    }

    const removeFileUploadContainer = (docName, docCode) => {
        if (!window.confirm(`You're about to remove "${docName}" and all its attachments. Continue?`)) return;

        const docToRemove = fileUploads.find(file => file.docCode === docCode);
        if (docToRemove?.files.length > 0) {
            docToRemove.files.forEach(file => removeFile(docCode, file.file, file.fileName));
        }

        const updatedFileUploads = fileUploads.filter(file => file.docCode !== docCode);
        setFileUploads(updatedFileUploads);
        updateFormData({ fileUploads: updatedFileUploads });
    }

    const handleUpload = (type) => {
        fileInputRef.current.setAttribute("doc-code", type);
        fileInputRef.current.click();
    };

    const removeFile = (docCode, fileToRemove, fileName) => {
        deleteFileFromServer(docCode, fileName);

        console.log("Removing file : " + fileName);
        const updatesFileUploads = fileUploads.map((upload) => {
            if (upload.docCode === docCode) {
                return {
                    ...upload,
                    files: upload.files.filter((file) => file.fileName !== fileName), // Remove the specific file
                };
            }
            return upload; // Keep other uploads unchanged
        });

        setFileUploads(updatesFileUploads)
        updateFormData({ fileUploads: updatesFileUploads });
    };

    const handleFileChange = async (event) => {
        const fileCode = event.target.getAttribute("doc-code");


        const existingDoc = fileUploads.find(upload => upload.docCode === fileCode);
        const newFiles = Array.from(event.target.files).filter(file => {
            if (!ALLOWED_FILE_TYPES.includes(file.type)) {
                alert(`The file "${file.name}" is not an allowed file type. Please upload files of type: ${ALLOWED_FILE_TYPES.join(", ")}`);
                return false;
            }
            if (file.size > MAX_FILE_SIZE) {
                alert(`The file "${file.name}" exceeds the size limit of ${MAX_FILE_SIZE_MB} MB. Please upload a smaller file.`);
                return false;
            }
            const fileExists = existingDoc && existingDoc.files.some(existingFile => existingFile.fileName === file.name);
            if (fileExists) {
                alert(`The file "${file.name}" has already been added to the list.`);
                return false;
            }
            return true;
        });

        if (newFiles.length === 0) {
            console.log("No new files to add or files already exist.");
            return; // No new files to add
        }

        // Add "status: idle" to each new file
        const filesWithStatus = newFiles.map(file => ({
            file: file,
            fileName: file.name,
            status: "idle", // Add default status
        }));

        const updatedUploads = fileUploads.map(upload => {
            if (upload.docCode === existingDoc.docCode) {
                return {
                    ...upload,
                    files: [
                        ...upload.files,
                        ...filesWithStatus, // Add new files to the existing upload
                    ]
                };
            }
            return upload;
        })

        setFileUploads(updatedUploads);
        updateFormData({ fileUploads: updatedUploads });

        // Upload each file and handle the result
        for (const file of newFiles) {
            try {
                console.log("Uploading file: ", file.name);
                const uploadResult = await uploadFileToServer(fileCode, file);
                console.log("Upload result for file", file.name, ":", uploadResult);
                if (uploadResult.status === true) {
                    // Update the file status to "uploaded"
                    const updatedUploadsAfterUpload = updatedUploads.map(upload => {
                        if (upload.docCode === fileCode) {
                            return {
                                ...upload,
                                files: upload.files.map(f => {
                                    if (f.fileName === file.name) {
                                        return { ...f, status: "uploaded" }; // Update status to "uploaded"
                                    }
                                    return f;
                                }),
                            };
                        }
                        return upload;
                    });

                    setFileUploads(updatedUploadsAfterUpload);
                    updateFormData({ fileUploads: updatedUploadsAfterUpload });
                } else { // Fail to Upload
                    alert(`Failed to upload file "${file.name}". Please re-upload the file.`);
                    const updatedUploadsAfterUpload = updatedUploads.map(upload => {
                        if (upload.docCode === fileCode) {
                            return {
                                ...upload,
                                files: upload.files.map(f => {
                                    if (f.fileName === file.name) {
                                        return { ...f, status: "error" }; // Update status to "uploaded"
                                    }
                                    return f;
                                }),
                            };
                        }
                        return upload;
                    });

                    setFileUploads(updatedUploadsAfterUpload);
                    updateFormData({ fileUploads: updatedUploadsAfterUpload });
                }
            } catch (error) {
                console.error("Error uploading file:", file.name, error);
            }
        }

        // Reset file input to allow re-selection of the same file
        event.target.value = "";
    };

    const uploadFileToServer = async (fileCode, fileToUpload) => {
        const newFileData = new FormData();
        newFileData.append('file', fileToUpload);
        newFileData.append('fileUploadID', formData.fileUploadID)
        newFileData.append('fileCode', fileCode)

        try {
            return await sendUpload("/upload-temp-file", "POST", newFileData);
        } catch (error) {
            console.error("Error uploading file:", error);
            return false;
        }
    }

    const deleteFileFromServer = async (fileCode, fileName) => {
        const newFileData = new FormData();
        newFileData.append('fileName', fileName);
        newFileData.append('fileUploadID', formData.fileUploadID)
        newFileData.append('fileCode', fileCode)
        try {
            const response = await callAPI("/delete-temp-file", "POST", newFileData);
            console.log("File deleted successfully:", response);
        } catch (error) {
            console.error("Error uploading file:", error);
        }
    };

    return (
        <Container>{injectFormProps(

            <Section title={"File Uploads"}>
                <i>Please upload {formData['workingWithSWIS'] === "1" ? " any " : <b>at least one</b>}  address verification document.</i>
                <i>
                    Allowed file types: <b>{ALLOWED_FILE_TYPES.map(type => type.split('/')[1]).join(', ')}</b>
                </i>
                <i>
                    Allowed file size: <b>Up to 5 MB per file</b>
                </i>
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }} // Hide file input
                    onChange={handleFileChange}
                    accept={ALLOWED_FILE_TYPES.join(',')} // Limit file types
                />
                <Input type="select" inputLabel={"Select a document type from the list below. You can add more by selecting one at a time."} options={selectOptions} mandatory={false}
                    onChange={(e) => addFileUploadContainer(e.target, e.target.options[e.target.selectedIndex].text, e.target.value)} />
                <Container>
                    <table className={"file-upload-table"}>
                        <thead>
                            <tr style={{ textAlign: "left" }}>
                                <th >Document Name</th>
                                <th>Attachment(s)</th>
                                <th>Actions</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {fileUploads.length === 0 ? (
                                <tr>
                                    <td className={"no-border"}>
                                        <i className={"file-upload-notes"}>No documents uploaded.</i>
                                    </td>
                                </tr>
                            )
                                :

                                fileUploads.map((fileUpload, index) => (
                                    <tr key={index}>
                                        <td className={"file-doc-name"}>{fileUpload.docName}</td>
                                        <td>
                                            {
                                                fileUpload.files.length > 0 ? (
                                                    fileUpload.files.map((file, fileIndex) => (
                                                        <div key={fileIndex} style={{ display: "inline-flex" }}>
                                                            <a href={file.file.name ? URL.createObjectURL(file.file) : "#"}
                                                                download={file.file.name}
                                                                style={{ marginRight: "5px" }}
                                                            >
                                                                {file.file.name ? file.file.name : file.fileName}
                                                            </a>
                                                            {
                                                                file.status === "idle"
                                                                    ? <svg className={"icon-spin"}
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        width={18}
                                                                        height={18}
                                                                        viewBox="0 0 24 24"
                                                                        fill="none"
                                                                        stroke="#000000"
                                                                        strokeWidth={2}
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round">
                                                                        <path d="M12 2v4" />
                                                                        <path d="m16.2 7.8 2.9-2.9" />
                                                                        <path d="M18 12h4" />
                                                                        <path d="m16.2 16.2 2.9 2.9" />
                                                                        <path d="M12 18v4" />
                                                                        <path d="m4.9 19.1 2.9-2.9" />
                                                                        <path d="M2 12h4" />
                                                                        <path d="m4.9 4.9 2.9 2.9" />
                                                                    </svg>
                                                                    : <button type="button" onClick={() => removeFile(fileUpload.docCode, file.file, file.fileName)}>X</button>
                                                            }
                                                        </div>
                                                    ))
                                                ) : (
                                                    <i className={"file-upload-notes"}>No files uploaded.</i>
                                                )
                                            }
                                        </td>
                                        <td>
                                            <FunctionButton type="button" classNames={"default-btn-r"} label={"Upload"} onClick={() => handleUpload(fileUpload.docCode)} />
                                        </td>
                                        <td>
                                            <button type="button" onClick={() => removeFileUploadContainer(fileUpload.docName, fileUpload.docCode)}>X</button>
                                        </td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                </Container>
                <hr />
                {!formData['workingWithSWIS'] && <i style={{ textAlign: "center" }}>If you're unable to provide the required documents at this time, you can submit them later by reaching out to your school.</i>}
            </Section>
        )
        }
        </Container>
    );
}

export default FileUploads;